import { createContext, useContext, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { ROLE_HIERARCHY } from '../constants/roles'
import { Feature } from '../types/enums'
import type { UserRole } from '../types'

interface RoleContextType {
  role: UserRole | null
  hasRole: (requiredRole: UserRole) => boolean
  isAdmin: () => boolean
  canAccess: (feature: Feature) => boolean
}

const RoleContext = createContext<RoleContextType | undefined>(undefined)

const FEATURE_ACCESS: Partial<Record<Feature, UserRole[]>> = {
  [Feature.PROJECTS]: ['inventor', 'innovateflow-admin'],
  [Feature.UPLOAD]: ['inventor', 'innovateflow-admin'],
  [Feature.CONNECTIONS]: ['inventor', 'innovateflow-admin'],
  [Feature.REVIEWS]: ['inventor', 'innovateflow-admin'],
  [Feature.USER_MANAGEMENT]: ['innovateflow-admin'],
  [Feature.AUDIT]: ['innovateflow-admin'],
  [Feature.SECURITY]: ['innovateflow-admin'],
}

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const role = user?.role || null

  const hasRole = (requiredRole: UserRole): boolean => {
    if (!role) return false
    return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[requiredRole]
  }

  // BUG-016: reconcile the two admin gates. AdminGuard (App.tsx) authorises
  // /admin on the Cognito group `innovateflow-admin`, but AdminLayout gates on
  // this isAdmin() which historically only read the `custom:role` claim. A user
  // who is admin ONLY via group (no custom:role) therefore passed AdminGuard yet
  // was bounced by AdminLayout. Honour the group as an equal source of truth so
  // both gates — and the Admin nav link (InventorLayout) — agree.
  const isAdmin = (): boolean =>
    hasRole('innovateflow-admin') || !!user?.groups?.includes('innovateflow-admin')

  const canAccess = (feature: Feature): boolean => {
    if (!role) return false
    const allowedRoles = FEATURE_ACCESS[feature]
    return allowedRoles ? allowedRoles.some((allowed) => hasRole(allowed)) : false
  }

  return (
    <RoleContext.Provider value={{ role, hasRole, isAdmin, canAccess }}>
      {children}
    </RoleContext.Provider>
  )
}

export function useRole(): RoleContextType {
  const context = useContext(RoleContext)
  if (!context) throw new Error('useRole must be used within RoleProvider')
  return context
}
